import React, { useState, useEffect } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'
import LoginForm from './LoginForm'
import Well from '../common/Well'
import PromoBanner from '../common/PromoBanner'
import Footer from '../layout/Footer'
import { login, clearLoginError } from '../../actions/login'
import { validateLogin } from '../../utils/validators'
import { showFullscreenLoader } from '../../actions/form'
import FullscreenLoader from '../common/FullscreenLoader'
import { ToastContainer, toast } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import adLeaderImage from '../../../assets/images/login_banner.jpg'
import styles from './common.module.css'

function Login({ appName, enableRegister, inProgress, error, currentUser, nextUrl, login, clearLoginError, showFullscreenLoader }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [validationErrors, setValidationErrors] = useState({})

  // Show toast when redirected here with ?loggedOut=true or ?passwordChanged=true
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    if (params.get('loggedOut') === 'true') {
      toast.success('You have been logged out successfully.', {
        position: 'top-right',
        autoClose: 3000,
      })
      window.history.replaceState({}, '', '/login')
    } else if (params.get('passwordChanged') === 'true') {
      toast.success('Password updated successfully. Please log in again.', {
        position: 'top-right',
        autoClose: 4000,
      })
      window.history.replaceState({}, '', '/login')
    }
  }, [])

  // Redirect after successful login
  useEffect(() => {
    if (currentUser) {
      showFullscreenLoader('Redirecting to dashboard...')
      toast.success('Login successful!', {
        position: 'top-right',
        autoClose: 1500,
        onClose: () => {
          window.location.href = nextUrl
        },
      })
    }
  }, [currentUser, nextUrl, showFullscreenLoader])

  const handleChange = fieldName => event => {
    const val = event.target.value
    // Clear only this field's validation error, leaving the other intact
    if (validationErrors[fieldName]) {
      setValidationErrors(prev => { const next = { ...prev }; delete next[fieldName]; return next })
    }
    // Clear API errors (401 etc.) on any input change
    if (error && Object.keys(error).length > 0) {
      clearLoginError()
    }
    if (fieldName === 'email') setEmail(val)
    if (fieldName === 'password') setPassword(val)
  }

  const handleSubmit = () => {
    const { valid, error: valErr } = validateLogin({ email, password })
    if (!valid) {
      setValidationErrors(valErr)
      return
    }
    setValidationErrors({})
    login(email, password)
  }

  return (
    <div id="extr-page">
      <FullscreenLoader />
      <ToastContainer
        position="top-right"
        autoClose={1000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />

      {/* Header */}
      <header id="header" className={styles.header}>
        <div className={styles.headerWrapper}>
          <div className={styles.logo} />
          <h1 className={styles.appName}>
            {appName}
          </h1>
          {enableRegister && (
            <div className={styles.actionButton}>
              <span className={styles.descriptor}>Need an account?</span>
              <Link to="/register">
                <button className="btn btn-primary">Register</button>
              </Link>
            </div>
          )}
        </div>
      </header>

      {/* Main form area */}
      <div id="main" role="main" className={styles.main}>
        <div className={styles.form}>
          <Well className="no-padding">
            <LoginForm
              onChangeFactory={handleChange}
              inProgress={inProgress}
              error={{ ...validationErrors, status: error && error.status, message: error && error.message }}
              onSubmit={handleSubmit}
            />
          </Well>
        </div>
      </div>

      <PromoBanner
        className={styles.ad}
        imgSrc={adLeaderImage}
        // url="https://mydiamond.app.link/login_leader"
      />

      <Footer />
    </div>
  )
}

const mapStateToProps = ({ login }) => ({
  currentUser: login.get('currentUser'),
  inProgress: login.get('inProgress'),
  nextUrl: login.get('nextUrl'),
  error: login.get('error') || {}
})

const mapDispatchToProps = {
  login,
  clearLoginError,
  showFullscreenLoader
}

export default connect(mapStateToProps, mapDispatchToProps)(Login)
